import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Auth } from '@aws-amplify/auth';
import { AuthService } from '../auth/auth.service';
import { CommonUtil } from '../util/common-util';
import { ToastUtil } from '../util/toast-util';
import { Constant } from '../constant';
import { SurveyListResponse, SurveyMemberListItem, SurveyManagerListItem } from '../model/survey';

@Component({
  selector: 'app-survey',
  templateUrl: './survey.component.html',
  styleUrl: './survey.component.css',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
  ]
})
export class SurveyComponent implements OnInit {
  public readonly Constant = Constant;
  public showSpinner = true;
  private apiPath = '/survey/list';

  // アンケート回答対象かどうかは、一覧取得APIが返すviewTypeで判定する。
  public isSurveyTarget = true;

  public memberItems: SurveyMemberListItem[] = [];
  public managerItems: SurveyManagerListItem[] = [];
  // 他組織列を表示するか（データの有無ではなく、権限「運用」以上かどうかで一覧取得APIが判定した値）
  public showOtherOrg = false;

  // 無制限ローディング（組合相談の一覧ページと同じ方式）。対象月が会社の運用年数分あり得るため、
  // 一度に全件は取得せず、下端に近づいたら次ページを読み込む
  private page = 0;
  private hasMore = true;
  private isLoading = false;

  constructor(
    private title: Title,
    private router: Router,
    private commonUtil: CommonUtil,
    private toastUtil: ToastUtil,
    private auth: AuthService,
  ) {
    this.title.setTitle(Constant.pageTitleSurvey + Constant.pageTitleCommon);
  }

  @HostListener('window:focus')
  onFocus(): void {
    this.commonUtil.checkRefreshToken(this.auth);
  }

  @HostListener('window:scroll')
  onScroll(): void {
    const scrollBottom = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
    if (scrollBottom < 300 && !this.isLoading && this.hasMore) {
      this.loadSurveyList();
    }
  }

  ngOnInit(): void {
    window.scroll(0, 0);
    this.loadSurveyList();
  }

  onAnswerClick(): void {
    this.router.navigate(['/survey/answer']);
  }

  onReportClick(rawMonth: string): void {
    // URLは "202609" のようにハイフン無しの形式で表示する
    this.router.navigate(['/survey/report', rawMonth.replace('-', '')]);
  }

  // レポートリンク有効化後なのに値が無い項目は「-」、それ以前はまだ確定していないためブランクのままにする
  blank(reportAvailable: boolean): string {
    return reportAvailable ? '-' : '';
  }

  private loadSurveyList(): void {
    if (this.isLoading || !this.hasMore) {
      return;
    }

    this.isLoading = true;
    if (this.page === 0) {
      this.showSpinner = true;
    }

    Auth.currentSession().then(session => {
      const options = this.auth.createApiHeader(session);
      const path = `${this.apiPath}?page=${this.page}&size=${Constant.surveyListPageSize}`;

      this.commonUtil.apiGet(path, options)
        .then(res => {
          const data: SurveyListResponse = res.data;

          this.isSurveyTarget = data.viewType === 'member';
          this.memberItems = [...this.memberItems, ...data.memberItems];
          this.managerItems = [...this.managerItems, ...data.managerItems];
          // データの有無ではなく、権限に基づくバックエンド側の判定をそのまま使う
          this.showOtherOrg = data.showOtherOrg;

          this.hasMore = data.hasMore;
          this.page++;

          this.showSpinner = false;
          this.isLoading = false;

          // 未回答の過去月は一覧から除外されるため、1ページの表示件数が0件になることがある。
          // その場合スクロールが発生せずonScrollが発火しないため、画面に収まる間は続けて読み込む
          if (this.hasMore && document.documentElement.scrollHeight <= window.innerHeight) {
            this.loadSurveyList();
          }
        })
        .catch(err => {
          this.commonUtil.debug().log(err);
          this.showSpinner = false;
          this.isLoading = false;
          if (err.data?.accessDenied) {
            this.router.navigate(['/access-denied']);
          } else if (err.message === Constant.msgLoginFailed) {
            this.commonUtil.showReloadDialog(this.auth);
          } else {
            this.toastUtil.showErrorToast('', Constant.msgNetworkError, Constant.toastShowMiliSec);
          }
        });
    });
  }
}
